import Button from '@viking-eng/button';
import CancelCircle from '@viking-eng/icon/lib/CancelCircle';
import PropTypes from '@viking-eng/prop-types';
import Spinner from '@viking-eng/spinner';
import classNames from 'classnames';
import React from 'react';
import ReactModal from 'react-modal';
import './Modal.css';

const Modal = ({
  allowScroll,
  children,
  className,
  closeAriaLabel = 'Close',
  disableCloseButton,
  fade,
  forceAction,
  forwardedRef,
  hasPreHeader,
  header,
  hideCloseButton,
  id,
  isFetching,
  isOpen,
  labelId,
  onAfterClose = () => null,
  onAfterOpen = () => null,
  onClose = () => null,
  onAfterPrint = () => null,
  onPrint,
  printAriaLabel,
  printHeaderImageUrl,
  printLabel,
  rootId = '#root',
  size = 'md',
  ...rest
}) => {
  if (rootId) {
    ReactModal.setAppElement(rootId);
  }

  const onHandlePrint = () => {
    if (onPrint) {
      onPrint();
      onAfterPrint();
    }
    return true;
  };

  return (
    <ReactModal
      id={id}
      isOpen={isOpen}
      onRequestClose={onClose}
      onAfterClose={onAfterClose}
      onAfterOpen={() => {
        const el = document.getElementsByClassName('new-modal-overlay')[0];
        if (el) {
          el.scrollTop = 0;
        }
        onAfterOpen();
      }}
      shouldCloseOnOverlayClick={!forceAction}
      shouldCloseOnEsc={!forceAction}
      className={classNames('new-modal', hasPreHeader ? 'has-pre-header' : '', className, size)}
      overlayClassName={classNames(
        'new-modal-overlay',
        allowScroll ? 'new-modal-overlay-with-scroll' : 'new-modal-overlay-without-scroll'
      )}
      bodyOpenClassName={allowScroll ? 'modal-scroll-allowed' : 'modal-scroll-not-allowed'}
      contentRef={(node) => {
        if (forwardedRef) {
          forwardedRef = node;
        }
      }}
      aria={{
        labelledby: labelId,
      }}

      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    >
      {header && <>{header()}</>}
      <div className={classNames('new-modal-content', header ? 'has-header' : '')}>
        {printHeaderImageUrl && (
          <div className="d-none d-print-block text-center">
            <img alt="viking logo" className="print-banner-img mx-auto w-10" src={printHeaderImageUrl} />
          </div>
        )}
        <div className={classNames('new-modal-controls-wrapper', forceAction ? 'force-action' : '')}>
          {onPrint && printLabel && (
            <div aria-label={printAriaLabel || printLabel} className="print-button align-baseline">
              <Button appearance="link" onButtonClick={onHandlePrint}>
                {printLabel}
              </Button>
            </div>
          )}
          {!forceAction && !hideCloseButton && (
            <div className="new-modal-controls align-baseline">
              <div className="close-button">
                <Button
                  appearance="icon"
                  ariaLabel={closeAriaLabel}
                  onButtonClick={disableCloseButton ? () => null : onClose}
                >
                  <CancelCircle />
                </Button>
              </div>
            </div>
          )}
        </div>
        {isFetching ? <Spinner color="red" /> : children}
      </div>
    </ReactModal>
  );
};

const ModalBody = ({ children }) => (children ? <div className="new-modal-body">{children}</div> : null);
ModalBody.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.jsx, PropTypes.node]),
};

const ModalFooter = ({ children }) =>
  children ? <div className="new-modal-footer pt-2 d-print-none">{children}</div> : null;
ModalFooter.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.jsx, PropTypes.node]),
};

const ModalTitle = ({ children, labelId }) =>
  children ? (
    <h1 id={labelId} className="h3 new-modal-title text-center pb-2 pt-1">
      {children}
    </h1>
  ) : null;
ModalTitle.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.jsx, PropTypes.node]),
  labelId: PropTypes.string,
};

const propTypes = {
  allowScroll: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.jsx, PropTypes.node]).isRequired,
  className: PropTypes.string,
  closeAriaLabel: PropTypes.string,
  disableCloseButton: PropTypes.bool,
  fade: PropTypes.bool,
  forceAction: PropTypes.bool,
  forwardedRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.instanceOf(Element) })]),
  hasPreHeader: PropTypes.bool,
  header: PropTypes.func,
  hideCloseButton: PropTypes.bool,
  id: PropTypes.string,
  isFetching: PropTypes.bool,
  isOpen: PropTypes.bool,
  labelId: PropTypes.string,
  onAfterClose: PropTypes.func,
  onAfterOpen: PropTypes.func,
  onClose: PropTypes.func,
  onAfterPrint: PropTypes.func,
  onPrint: PropTypes.func,
  printAriaLabel: PropTypes.string,
  printHeaderImageUrl: PropTypes.string,
  printLabel: PropTypes.string,
  rootId: PropTypes.string,
  size: PropTypes.oneOf(['md', 'sm']),
};

Modal.propTypes = propTypes;
Modal.displayName = 'Modal';

export default Modal;

export { ModalBody, ModalFooter, ModalTitle, propTypes };
